// UCSF-3818: the postMessage contract between the embedded avatar surface (this GritxSite route,
// loaded in an iframe) and its parent (the admin-web Experiments run page, UCSF-3796).
//
// The embed self-authenticates via its own Auth0 client, so NO token is ever passed across the
// bridge. Only lifecycle events travel parent-ward, and they carry no PHI. Keep this file and the
// admin-web consumer in step; bump BRIDGE_VERSION on any breaking change.

export const BRIDGE_SOURCE = 'gritx-avatar-embed';
export const BRIDGE_VERSION = 1;

export type AvatarEmbedEvent =
  // The surface has mounted and the viewer is authenticated; the lobby is visible. The parent may
  // reveal the iframe.
  | {type: 'ready'}
  // No verbalize.ai session is available in this browser, so the surface cannot authenticate
  // silently. The parent can prompt the viewer to sign in to verbalize.ai first.
  | {type: 'auth-required'}
  // The live conversation ended (the viewer left, the provider session closed, or it errored). The
  // parent should refresh its History view. `reason` is the videocomponent end reason, passed
  // through untyped so the bridge does not couple to the component's internals.
  | {type: 'session-ended'; reason?: string};

export interface AvatarEmbedMessage {
  source: typeof BRIDGE_SOURCE;
  v: typeof BRIDGE_VERSION;
  event: AvatarEmbedEvent;
}

// Allowed parent origins, from REACT_APP_EMBED_PARENT_ORIGINS (comma-separated). When unset we fall
// back to '*': the payloads carry no secrets, but configuring the allowlist is recommended in
// deployed environments.
function parentOrigins(): string[] {
  const configured = (process.env.REACT_APP_EMBED_PARENT_ORIGINS || '')
    .split(',')
    .map(o => o.trim())
    .filter(Boolean);

  return configured.length ? configured : ['*'];
}

export function postToParent(event: AvatarEmbedEvent): void {
  if (typeof window === 'undefined' || window.parent === window) {
    return;
  }

  const message: AvatarEmbedMessage = {source: BRIDGE_SOURCE, v: BRIDGE_VERSION, event};

  parentOrigins().forEach(origin => {
    try {
      window.parent.postMessage(message, origin);
    } catch (error) {
      console.error('avatar-embed postToParent failed', {origin, error});
    }
  });
}
